import { useEffect, useState } from 'react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import {
  CalendarClock, Camera, MapPin, Send, Trash2, XCircle,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, redirect } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import {
  PUBLICATION_STATE_FR, estErreurApi, heureFr,
  type PublicationRow,
} from './lib.js'

/**
 * PUBLICATIONS — /communication/publications (+ composer pleine page).
 *
 * L'import d'une soirée n'apporte QUE ce que la soirée sait (§49) : nom,
 * début, fin si canonique, lien billetterie. Aucun visuel inventé, aucun
 * « 06:00 » sorti de nulle part.
 *
 * SÉMANTIQUE DU MODE SÛR (§35) : programmer en mode sûr donne l'état
 * « En attente d'autorisation d'écriture » — jamais un faux « Programmé »
 * qui laisserait croire qu'un envoi partira.
 */

const CANAUX = [
  { key: 'instagram', label: 'Instagram', Icon: Camera },
  { key: 'google_business', label: 'Google Business', Icon: MapPin },
] as const

function pillEtat(state: string) {
  const label = PUBLICATION_STATE_FR[state] ?? state
  const cls = state === 'PUBLISHED' ? 'lc-pill--ok'
    : state === 'FAILED' || state === 'UNCERTAIN' ? 'lc-pill--error'
    : state === 'SCHEDULED' || state === 'AWAITING_WRITE_ACCESS' || state === 'PUBLISHING' ? 'lc-pill--warning'
    : 'lc-pill--idle'
  return <span className={`lc-pill ${cls}`}>{label}</span>
}

/* ═══════════════════ liste /communication/publications ═════════════════ */

const VUES = [
  { value: 'upcoming', label: 'À venir' },
  { value: 'drafts', label: 'Brouillons' },
  { value: 'published', label: 'Publiées' },
  { value: 'errors', label: 'À vérifier' },
  { value: 'all', label: 'Toutes' },
] as const

export function CommPublicationsPage() {
  const meta = useShellMeta()
  const [vue, setVue] = useState<(typeof VUES)[number]['value']>('upcoming')
  const [items, setItems] = useState<PublicationRow[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    setItems(null)
    void apiGet<{ items: PublicationRow[] } | { error: string; message?: string }>(
      `/api/v1/communication/publications?view=${vue}`)
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setItems(j.items); setErreur(null) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [vue])

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Publications</h1>
            <p className="lc-subtitle">Préparer une fois, adapter par canal, publier quand le portail le permet.</p>
          </div>
          <div className="cm-head__actions">
            <div className="lc-seg cm-periods" role="group" aria-label="Vue">
              {VUES.map((v) => (
                <button key={v.value} type="button"
                  className={`lc-seg__btn ${vue === v.value ? 'lc-seg__btn--on' : ''}`}
                  aria-pressed={vue === v.value}
                  onClick={() => setVue(v.value)}>{v.label}</button>
              ))}
            </div>
            <Link href="/communication/publications/nouvelle" className="lc-btn lc-btn--primary">
              <Send size={14} aria-hidden="true" /> Nouvelle publication
            </Link>
          </div>
        </header>

        {erreur ? <p className="lc-hint">{erreur}</p> : null}
        {items === null && !erreur ? <p className="lc-hint">Chargement…</p> : null}
        {items && items.length === 0 ? (
          <p className="lc-hint">
            {vue === 'upcoming' ? 'Rien de programmé ni en attente.'
              : vue === 'errors' ? 'Aucune publication en échec ni à vérifier.'
              : 'Aucune publication dans cette vue.'}
          </p>
        ) : null}

        <div className="cm-publist">
          {(items ?? []).map((p) => (
            <Link key={p.id} href={`/communication/publications/${p.id}`} className="lc-card cm-pubcard">
              <span className="lc-card__edge" aria-hidden="true" />
              <div className="cm-pubcard__top">
                <strong>{p.title || 'Sans titre'}</strong>
                {pillEtat(p.state)}
              </div>
              <p className="cm-pubcard__body lc-hint">{p.body ? (p.body.length > 140 ? `${p.body.slice(0, 140)}…` : p.body) : 'Corps vide.'}</p>
              <div className="cm-pubcard__foot">
                <span className="cm-pubcard__channels">
                  {p.channels.map((c) => {
                    const def = CANAUX.find((x) => x.key === c)
                    return def ? <def.Icon key={c} size={14} aria-label={def.label} /> : null
                  })}
                  {p.channels.length === 0 ? <span className="lc-hint">aucun canal</span> : null}
                </span>
                {p.scheduled_at ? <span className="lc-hint"><CalendarClock size={13} aria-hidden="true" /> {heureFr(p.scheduled_at)}</span> : null}
              </div>
            </Link>
          ))}
        </div>
      </main>
    </Shell>
  )
}

/* ═══════════════════ composer (nouvelle + édition) ═════════════════════ */

function ComposerContent({ id }: { id: string | null }) {
  const toast = useToast()
  const permissions = usePermissions()
  const peutPublier = permissions.permissions.includes('communication.publish')
  const [pub, setPub] = useState<PublicationRow | null>(null)
  const [titre, setTitre] = useState('')
  const [corps, setCorps] = useState('')
  const [canaux, setCanaux] = useState<string[]>([])
  const [adaptations, setAdaptations] = useState<Record<string, { title?: string; body?: string }>>({})
  const [quand, setQuand] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmerSuppression, setConfirmerSuppression] = useState(false)
  // Import de soirée : proposé à la création uniquement.
  const [soirees, setSoirees] = useState<{ id: string; name: string; starts_at: string }[] | null>(null)

  const modifiable = pub === null || pub.state === 'DRAFT' || pub.state === 'READY'

  useEffect(() => {
    if (!id) return
    void apiGet<PublicationRow | { error: string; message?: string }>(`/api/v1/communication/publications/${id}`)
      .then((j) => {
        if (estErreurApi(j)) { redirect('/communication/publications'); return }
        setPub(j); setTitre(j.title); setCorps(j.body); setCanaux(j.channels); setAdaptations(j.adaptations)
        if (j.scheduled_at) {
          // datetime-local attend l'heure LOCALE sans fuseau.
          const d = new Date(j.scheduled_at)
          const pad = (n: number) => String(n).padStart(2, '0')
          setQuand(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
        }
      })
      .catch(() => redirect('/communication/publications'))
  }, [id])

  // Soirées à venir pour l'import — la liste existante, aucune donnée inventée.
  useEffect(() => {
    if (id) return
    void apiGet<{ upcoming?: { id: string; name: string; starts_at: string }[] }>('/api/v1/soirees')
      .then((j) => setSoirees((j.upcoming ?? []).slice(0, 12)))
      .catch(() => setSoirees([]))
  }, [id])

  const importerSoiree = async (eventId: string) => {
    const d = await apiGet<{ name?: string; starts_at?: string; ends_at?: string | null; ticket_url?: string | null; error?: string }>(
      `/api/v1/communication/event-import/${eventId}`)
    if (estErreurApi(d) || !d.name || !d.starts_at) { toast('error', 'Import impossible.'); return }
    const debut = new Date(d.starts_at).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    })
    setTitre(d.name)
    // On n'écrit QUE ce que la soirée sait : pas de fin inventée.
    setCorps([
      `${d.name}`,
      `${debut}${d.ends_at ? ` → ${new Date(d.ends_at).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })}` : ''}`,
      d.ticket_url ? `Billets : ${d.ticket_url}` : '',
    ].filter(Boolean).join('\n'))
    toast('ok', 'Informations de la soirée importées — rien d’autre que ce qu’elle sait.')
  }

  const enregistrer = async (): Promise<string | null> => {
    setBusy(true)
    if (pub) {
      const r = await adminMutate<PublicationRow & { error?: string; message?: string }>(
        'PUT', `/api/v1/communication/publications/${pub.id}`,
        { title: titre, body: corps, channels: canaux, adaptations })
      setBusy(false)
      if (r.status !== 200) { toast('error', r.data.message ?? 'Enregistrement impossible.'); return null }
      setPub(r.data); toast('ok', 'Brouillon enregistré.')
      return r.data.id
    }
    const r = await adminMutate<PublicationRow & { error?: string; message?: string }>(
      'POST', '/api/v1/communication/publications', { title: titre, body: corps, channels: canaux })
    setBusy(false)
    if (r.status !== 200 || !r.data.id) { toast('error', r.data.message ?? 'Création impossible.'); return null }
    toast('ok', 'Brouillon créé.')
    redirect(`/communication/publications/${r.data.id}`)
    return r.data.id
  }

  const programmer = async () => {
    if (!quand) { toast('error', 'Choisir un horaire d’abord.'); return }
    const idCible = await enregistrer()
    if (!idCible) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; ok?: boolean; state?: string; message?: string }>(
      'POST', `/api/v1/communication/publications/${idCible}/schedule`,
      { at: new Date(quand).toISOString() })
    setBusy(false)
    if (r.status !== 200) { toast('error', r.data.message ?? 'Programmation impossible.'); return }
    toast('ok', r.data.state === 'AWAITING_WRITE_ACCESS'
      ? 'Enregistré — en attente d’autorisation d’écriture (mode sûr) : rien ne partira sans elle.'
      : 'Publication programmée.')
    redirect('/communication/publications')
  }

  if (!peutPublier) {
    return <p className="lc-hint">Préparer des publications demande le droit « publier » (direction).</p>
  }
  if (id && !pub) return <p className="lc-hint">Chargement…</p>

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">{pub ? (pub.title || 'Publication') : 'Nouvelle publication'}</h1>
          <p className="lc-subtitle">
            {pub ? <>{pillEtat(pub.state)}{pub.scheduled_at ? <> · prévu {heureFr(pub.scheduled_at)}</> : null}</>
              : 'Un message, des canaux — chaque canal peut adapter son texte.'}
          </p>
        </div>
      </header>

      {!id && soirees && soirees.length > 0 ? (
        <section className="lc-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Partir d’une soirée</h2></header>
          <div className="lc-panel__body cm-eventimport">
            {soirees.map((s) => (
              <button key={s.id} type="button" className="lc-btn" onClick={() => void importerSoiree(s.id)}>
                {s.name} · {new Date(s.starts_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'short' })}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="lc-panel">
        <header className="lc-panel__head"><h2 className="lc-section-title">Message</h2></header>
        <div className="lc-panel__body cm-credform">
          <Field label="Titre">
            <TextInput value={titre} onChange={setTitre} maxLength={300} disabled={!modifiable} />
          </Field>
          <Field label="Texte" helper="Le texte commun — chaque canal peut ensuite l'adapter.">
            <textarea className="lc-input cm-replybox" rows={6} value={corps} maxLength={5000}
              disabled={!modifiable} onChange={(e) => setCorps(e.target.value)} aria-label="Texte de la publication" />
          </Field>
          <div className="cm-channels" role="group" aria-label="Canaux">
            {CANAUX.map((c) => {
              const actif = canaux.includes(c.key)
              return (
                <button key={c.key} type="button" disabled={!modifiable}
                  className={`cm-channel ${actif ? 'cm-channel--on' : ''}`}
                  aria-pressed={actif}
                  onClick={() => setCanaux(actif ? canaux.filter((x) => x !== c.key) : [...canaux, c.key])}>
                  <c.Icon size={15} aria-hidden="true" /> {c.label}
                </button>
              )
            })}
          </div>
          {canaux.map((c) => {
            const def = CANAUX.find((x) => x.key === c)
            return (
              <Field key={c} label={`Adaptation ${def?.label ?? c} (optionnelle)`}
                helper="Vide = le texte commun est utilisé tel quel.">
                <textarea className="lc-input cm-replybox" rows={3} maxLength={5000} disabled={!modifiable}
                  value={adaptations[c]?.body ?? ''} aria-label={`Adaptation ${def?.label ?? c}`}
                  onChange={(e) => setAdaptations({ ...adaptations, [c]: { ...adaptations[c], body: e.target.value } })} />
              </Field>
            )
          })}
        </div>
      </section>

      {modifiable ? (
        <section className="lc-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Programmation</h2></header>
          <div className="lc-panel__body cm-credform">
            <Field label="Publier le" helper="Heure de Paris. En mode sûr, la publication attendra l'autorisation d'écriture — état affiché honnêtement.">
              <input className="lc-input" type="datetime-local" value={quand}
                onChange={(e) => setQuand(e.target.value)} aria-label="Horaire de publication" />
            </Field>
            <div className="cm-replyactions">
              <button type="button" className="lc-btn" disabled={busy} onClick={() => void enregistrer()}>
                Enregistrer le brouillon
              </button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={busy || canaux.length === 0}
                onClick={() => void programmer()}>
                <CalendarClock size={14} aria-hidden="true" /> Programmer
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {pub ? (
        <section className="lc-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Cycle de vie</h2></header>
          <div className="lc-panel__body">
            {Object.keys(pub.provider_results).length > 0 ? (
              <ul className="cm-actionlist">
                {Object.entries(pub.provider_results).map(([canal, r]) => (
                  <li key={canal}>
                    <span className="cm-actionlist__label">{CANAUX.find((c) => c.key === canal)?.label ?? canal}</span>
                    <span className="lc-hint">{r.state ?? '—'}{r.reason ? ` · ${r.reason}` : ''}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="lc-hint">Aucune tentative d’envoi pour l’instant.</p>}
            <div className="cm-replyactions">
              {['SCHEDULED', 'AWAITING_WRITE_ACCESS'].includes(pub.state) ? (
                <button type="button" className="lc-btn" disabled={busy} onClick={async () => {
                  setBusy(true)
                  const r = await adminMutate<{ error?: string; ok?: boolean; message?: string }>(
                    'POST', `/api/v1/communication/publications/${pub.id}/cancel`)
                  setBusy(false)
                  if (r.status === 200 && r.data.ok) { toast('ok', 'Publication annulée.'); redirect('/communication/publications') }
                  else toast('error', r.data.message ?? 'Annulation impossible.')
                }}>
                  <XCircle size={14} aria-hidden="true" /> Annuler la programmation
                </button>
              ) : null}
              {modifiable ? (
                <button type="button" className="lc-btn lc-btn--danger" disabled={busy}
                  onClick={() => setConfirmerSuppression(true)}>
                  <Trash2 size={14} aria-hidden="true" /> Supprimer le brouillon
                </button>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {pub ? (
        <ConfirmDialog open={confirmerSuppression} danger title="Supprimer ce brouillon ?"
          confirmLabel="Supprimer" cancelLabel="Garder le brouillon"
          onClose={() => setConfirmerSuppression(false)}
          onConfirm={async () => {
            setConfirmerSuppression(false); setBusy(true)
            const r = await adminMutate<{ error?: string; ok?: boolean; message?: string }>(
              'DELETE', `/api/v1/communication/publications/${pub.id}`)
            setBusy(false)
            if (r.status === 200 && r.data.ok) { toast('ok', 'Brouillon supprimé.'); redirect('/communication/publications') }
            else toast('error', r.data.message ?? 'Suppression impossible.')
          }}>
          La suppression est définitive — le texte ne sera pas conservé.
        </ConfirmDialog>
      ) : null}
    </>
  )
}

export function CommComposerPage({ id }: { id: string | null }) {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback="/communication/publications" />
        <ToastProvider><ComposerContent id={id} /></ToastProvider>
      </main>
    </Shell>
  )
}
